import { FunctionComponent, lazy, useEffect } from 'react';
import { graphql, useFragment } from 'react-relay';
import { FormattedMessage } from 'dibs-react-intl';
import { useCheckoutSelector } from './reducers/root';
import {
    isOnShippingPane,
    isOnDeliveryPane,
    isOnPaymentPane,
    isOnReviewPane,
} from './reducers/checkoutPaneReducer';

import { CheckoutNavigation } from './CheckoutNavigation';
import { CheckoutProgressReview } from './CheckoutProgressReview';
import CheckoutShippingStep from './shipping/CheckoutShippingStep';
import { CheckoutReviewStep } from './review/CheckoutReviewStep';
import { PreloadedDuties } from './delivery/usePreloadedDuties';
import { CheckoutDeliveryStepSuspense } from './delivery/CheckoutDeliveryStepSuspense';
import { triggerSuspense } from './helpers/triggerSuspense';
import { usePrerenderedCheckoutStore } from './prerender/prerenderedCheckoutStoreReact';

import { CheckoutSteps_viewer$key } from './__generated__/CheckoutSteps_viewer.graphql';
import { CheckoutSteps_item$key } from './__generated__/CheckoutSteps_item.graphql';
import { CheckoutSteps_buyer$key } from './__generated__/CheckoutSteps_buyer.graphql';
import { CheckoutSteps_transaction$key } from './__generated__/CheckoutSteps_transaction.graphql';
import { CheckoutSteps_preCheckoutTransaction$key } from './__generated__/CheckoutSteps_preCheckoutTransaction.graphql';
import { CheckoutSteps_deliveryOptions$key } from './__generated__/CheckoutSteps_deliveryOptions.graphql';

import styles from './styles/CheckoutSteps.scss';
import dibsCss from 'dibs-css';

const CheckoutDeliveryStep = lazy(
    () =>
        import(
            /* webpackChunkName: "CheckoutDeliveryStep", webpackPrefetch: true */ './delivery/CheckoutDeliveryStep'
        )
);

const CheckoutPaymentStep = lazy(
    () =>
        import(
            /* webpackChunkName: "CheckoutPaymentStep", webpackPrefetch: true */ './payment/CheckoutPaymentStep'
        )
);

const CheckoutPaymentStepSelector = lazy(
    () =>
        import(
            /* webpackChunkName: "CheckoutPaymentStepSelector", webpackPrefetch: true */ './payment/CheckoutPaymentStepSelector'
        )
);

const viewerFragment = graphql`
    fragment CheckoutSteps_viewer on Viewer {
        ...CheckoutShippingStep_viewer
        ...CheckoutReviewStep_viewer
        ...CheckoutDeliveryStep_viewer
        ...CheckoutPaymentStep_viewer
        ...CheckoutPaymentStepSelector_viewer
        isPaymentMethodSelectorEnabled: featureFlag(feature: "checkoutPaymentMethodSelector")
    }
`;

const itemFragment = graphql`
    fragment CheckoutSteps_item on Item {
        ...CheckoutDeliveryStep_item
        ...CheckoutReviewStep_item
        ...CheckoutProgressReview_item
        ...CheckoutPaymentStep_item
    }
`;

const buyerFragment = graphql`
    fragment CheckoutSteps_buyer on User {
        ...CheckoutShippingStep_buyer
        ...CheckoutDeliveryStep_buyer
        ...CheckoutReviewStep_buyer
        ...CheckoutNavigation_buyer
        ...CheckoutProgressReview_buyer
        ...CheckoutPaymentStep_buyer
        ...CheckoutPaymentStepSelector_buyer
    }
`;

const deliveryOptionsFragment = graphql`
    fragment CheckoutSteps_deliveryOptions on ShipmentQuote @relay(plural: true) {
        ...CheckoutDeliveryStep_deliveryOptions
        ...CheckoutProgressReview_deliveryOptions
        ...CheckoutReviewStep_deliveryOptions
    }
`;

const transactionFragment = graphql`
    fragment CheckoutSteps_transaction on Transaction {
        ...CheckoutShippingStep_transaction
        ...CheckoutReviewStep_transaction
        ...CheckoutDeliveryStep_transaction
        ...CheckoutNavigation_transaction
        ...CheckoutProgressReview_transaction
        ...CheckoutPaymentStep_transaction
        ...CheckoutPaymentStepSelector_transaction
    }
`;

const preCheckoutTransactionFragment = graphql`
    fragment CheckoutSteps_preCheckoutTransaction on Transaction {
        ...CheckoutDeliveryStep_preCheckoutTransaction
        ...CheckoutReviewStep_preCheckoutTransaction
    }
`;

type CheckoutStepsProps = {
    viewer: CheckoutSteps_viewer$key | null;
    item: CheckoutSteps_item$key | null;
    buyer: CheckoutSteps_buyer$key | null;
    transaction: CheckoutSteps_transaction$key | null;
    deliveryOptions: CheckoutSteps_deliveryOptions$key | null;
    preCheckoutTransaction: CheckoutSteps_preCheckoutTransaction$key | null;
    isComputing: boolean;
    preloadedDuties: PreloadedDuties;
    hideNavigation?: boolean;
};

export const CheckoutSteps: FunctionComponent<CheckoutStepsProps> = props => {
    const { isComputing, preloadedDuties, hideNavigation } = props;

    const viewer = useFragment(viewerFragment, props.viewer);
    const item = useFragment(itemFragment, props.item);
    const buyer = useFragment(buyerFragment, props.buyer);
    const transaction = useFragment(transactionFragment, props.transaction);
    const deliveryOptions = useFragment(deliveryOptionsFragment, props.deliveryOptions);
    const preCheckoutTransaction = useFragment(
        preCheckoutTransactionFragment,
        props.preCheckoutTransaction
    );

    let ResolvedPaymentStep = null;
    if (viewer?.isPaymentMethodSelectorEnabled) {
        ResolvedPaymentStep = CheckoutPaymentStepSelector;
    } else {
        ResolvedPaymentStep = CheckoutPaymentStep;
    }

    const paneState = useCheckoutSelector(state => state.pane);
    const isReadyAfterDeliveryOptionsFetch = useCheckoutSelector(
        state => state.ui.isReadyAfterDeliveryOptionsFetch
    );
    const isAuthChecked = useCheckoutSelector(state => state.ui.authState === 'CHECKED');
    const { rootState } = usePrerenderedCheckoutStore();
    /**
     * NOT_INITIALIZED - we're on the old/faster checkout.
     * VISIBLE - we're on the pre-renderd checkout.
     */
    const isCheckoutActive = ['NOT_INITIALIZED', 'VISIBLE'].includes(rootState);

    useEffect(() => {
        if (isCheckoutActive) {
            window.scrollTo({ top: 0 });
        }
    }, [paneState.currentPane, isCheckoutActive]);

    /**
     * The important check here is `isReadyAfterDeliveryOptionsFetch` and `isAuthChecked`, everything else is just typescript inference.
     */

    if (
        !isReadyAfterDeliveryOptionsFetch ||
        !isAuthChecked ||
        !item ||
        !deliveryOptions ||
        !viewer
    ) {
        triggerSuspense();
    }

    let paneToRender = null;

    if (isOnShippingPane(paneState)) {
        paneToRender = (
            <>
                <div className={`${dibsCss.flex} ${dibsCss.justifyBetween} ${dibsCss.itemsCenter}`}>
                    <h1 className={styles.stepHeader}>
                        <FormattedMessage
                            id="checkout.CheckoutSteps.shipping"
                            defaultMessage="Shipping Address"
                        />
                    </h1>
                </div>

                <CheckoutShippingStep
                    isComputing={isComputing}
                    viewer={viewer}
                    transaction={transaction}
                    buyer={buyer}
                />
            </>
        );
    } else if (isOnDeliveryPane(paneState)) {
        paneToRender = (
            <>
                <h1 className={styles.stepHeader}>
                    <FormattedMessage
                        id="checkout.CheckoutSteps.delivery"
                        defaultMessage="Shipping Method"
                    />
                </h1>
                <CheckoutDeliveryStepSuspense />
                <CheckoutDeliveryStep
                    isComputing={isComputing}
                    viewer={viewer}
                    item={item}
                    buyer={buyer}
                    transaction={transaction}
                    deliveryOptions={deliveryOptions}
                    preloadedDuties={preloadedDuties}
                    preCheckoutTransaction={preCheckoutTransaction}
                    dataTn="shipping-method-pane"
                />
            </>
        );
    } else if (isOnPaymentPane(paneState) && buyer) {
        paneToRender = (
            <>
                <h1 className={styles.stepHeader}>
                    <FormattedMessage
                        id="checkout.CheckoutSteps.payment"
                        defaultMessage="Payment Method"
                    />
                </h1>
                <ResolvedPaymentStep
                    isComputing={isComputing}
                    viewer={viewer}
                    transaction={transaction}
                    buyer={buyer}
                    item={item}
                />
            </>
        );
    } else if (isOnReviewPane(paneState) && buyer && transaction) {
        paneToRender = (
            <CheckoutReviewStep
                isComputing={isComputing}
                viewer={viewer}
                item={item}
                buyer={buyer}
                transaction={transaction}
                deliveryOptions={deliveryOptions}
                preCheckoutTransaction={preCheckoutTransaction}
                preloadedDuties={preloadedDuties}
            />
        );
    } else {
        triggerSuspense();
    }

    return (
        <>
            {!hideNavigation && (
                <CheckoutNavigation
                    buyer={buyer}
                    transaction={transaction}
                    isComputing={isComputing}
                />
            )}
            <CheckoutProgressReview
                isComputing={isComputing}
                item={item}
                buyer={buyer}
                transaction={transaction}
                deliveryOptions={deliveryOptions}
            />
            {paneToRender}
        </>
    );
};
